import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
// @ts-ignore
import ImageModule from 'docxtemplater-image-module-free';
import { getImageFromS3 } from './cleaningExcel';

async function rotateImage90Degrees(imageUrl: string, extension: string): Promise<ArrayBuffer | null> {
    return new Promise((resolve) => {
        try {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.referrerPolicy = 'no-referrer';
            
            img.onload = () => {
                try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.height;
                    canvas.height = img.width;
                    const ctx = canvas.getContext('2d');
                    
                    if (!ctx) {
                        resolve(null);
                        return;
                    }
                    
                    ctx.translate(canvas.width / 2, canvas.height / 2);
                    ctx.rotate(Math.PI / 2);
                    ctx.drawImage(img, -img.width / 2, -img.height / 2);
                    
                    canvas.toBlob((blob) => {
                        if (!blob) {
                            resolve(null);
                            return;
                        }
                        
                        blob.arrayBuffer().then((arrayBuffer) => {
                            resolve(arrayBuffer);
                        }).catch(() => {
                            resolve(null);
                        });
                    }, extension === 'png' ? 'image/png' : 'image/jpeg');
                } catch (error) {
                    resolve(null);
                }
            };
            
            img.onerror = () => {
                resolve(null);
            };
            
            img.src = imageUrl;
        } catch (error) {
            resolve(null);
        }
    });
}

/**
 * 從圖片 buffer 中讀取圖片尺寸
 * @param buffer 圖片 buffer
 * @param extension 圖片擴展名
 * @returns [width, height] 或 null
 */
function getImageDimensionsFromBuffer(buffer: ArrayBuffer, extension: string): [number, number] | null {
    try {
        const view = new DataView(buffer);
        
        if (extension === 'png') {
            // PNG: 前 8 字節是簽名，然後是 IHDR chunk
            // IHDR chunk 的前 4 字節是寬度，接下來 4 字節是高度
            if (buffer.byteLength < 24) return null;
            const width = view.getUint32(16, false); // big-endian
            const height = view.getUint32(20, false); // big-endian
            return [width, height];
        } else if (extension === 'jpeg' || extension === 'jpg') {
            // JPEG: 查找 SOF (Start of Frame) marker
            // 0xFF 0xC0, 0xFF 0xC1, 0xFF 0xC2, etc.
            let offset = 2; // 跳過前兩個字節 (FF D8)
            
            while (offset < buffer.byteLength - 1) {
                if (view.getUint8(offset) === 0xFF) {
                    const marker = view.getUint8(offset + 1);
                    // SOF markers: 0xC0-0xC3, 0xC5-0xC7, 0xC9-0xCB, 0xCD-0xCF
                    if ((marker >= 0xC0 && marker <= 0xC3) || 
                        (marker >= 0xC5 && marker <= 0xC7) || 
                        (marker >= 0xC9 && marker <= 0xCB) || 
                        (marker >= 0xCD && marker <= 0xCF)) {
                        // 高度在 offset + 5 (2 bytes)
                        // 寬度在 offset + 7 (2 bytes)
                        if (offset + 9 <= buffer.byteLength) {
                            const height = view.getUint16(offset + 5, false); // big-endian
                            const width = view.getUint16(offset + 7, false); // big-endian
                            return [width, height];
                        }
                    }
                }
                offset++;
            }
        }
        return null;
    } catch (error) {
        return null;
    }
}

async function downloadImageToBuffer(imagePath: string, rotate: boolean = true): Promise<{
    buffer: ArrayBuffer;
    extension: string;
    width?: number;
    height?: number;
} | null> {
    try {
        let extension = 'jpeg';
        const imagePathLower = imagePath.toLowerCase();
        if (imagePathLower.includes('.png')) {
            extension = 'png';
        } else if (imagePathLower.includes('.jpg') || imagePathLower.includes('.jpeg')) {
            extension = 'jpeg';
        }
        
        let imageUrl: string;
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
            imageUrl = imagePath;
        } else {
            imageUrl = `${process.env.REACT_APP_S3}${imagePath}`;
        }
        
        let imageBuffer: ArrayBuffer | null = null;
        let width: number | undefined;
        let height: number | undefined;
        
        if (rotate) {
            const rotatedBuffer = await rotateImage90Degrees(imageUrl, extension);
            if (!rotatedBuffer || rotatedBuffer.byteLength === 0) {
                const fallbackBuffer = await getImageFromS3(imagePath);
                if (!fallbackBuffer || fallbackBuffer.byteLength === 0) {
                    return null;
                }
                imageBuffer = fallbackBuffer;
            } else {
                imageBuffer = rotatedBuffer;
                const originalBuffer = await getImageFromS3(imagePath);
                if (originalBuffer) {
                    const originalDimensions = getImageDimensionsFromBuffer(originalBuffer, extension);
                    if (originalDimensions) {
                        width = originalDimensions[1];
                        height = originalDimensions[0];
                    }
                }
            }
        } else {
            const originalBuffer = await getImageFromS3(imagePath);
            if (!originalBuffer || originalBuffer.byteLength === 0) {
                return null;
            }
            imageBuffer = originalBuffer;
            
            const dimensions = getImageDimensionsFromBuffer(imageBuffer, extension);
            if (dimensions) {
                width = dimensions[0];
                height = dimensions[1];
            } else {
                try {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.referrerPolicy = 'no-referrer';
                    
                    const imageDimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
                        const timeout = setTimeout(() => {
                            reject(new Error('Timeout'));
                        }, 5000);
                        
                        img.onload = () => {
                            clearTimeout(timeout);
                            resolve({ width: img.naturalWidth, height: img.naturalHeight });
                        };
                        img.onerror = () => {
                            clearTimeout(timeout);
                            reject(new Error('Failed to load image'));
                        };
                        img.src = imageUrl;
                    });
                    
                    width = imageDimensions.width;
                    height = imageDimensions.height;
                } catch (error) {
                }
            }
        }
        
        if (!imageBuffer) {
            return null;
        }
        
        return {
            buffer: imageBuffer,
            extension,
            width,
            height
        };
    } catch (error) {
        return null;
    }
}

async function processInspectionData(data: {
    type: string;
    contractNumber?: string;
    tableRows: Array<Record<string, any>>;
}) {
    const processedTableRows = await Promise.all(
        data.tableRows.map(async (row) => {
            const processPhotoArray = async (photos: Array<{ image: string }>) => {
                if (!photos || photos.length === 0) {
                    return [];
                }
                
                const processedPhotos = await Promise.all(
                    photos.map(async (photo) => {
                        if (!photo.image) {
                            return null;
                        }
                        
                        const imageData = await downloadImageToBuffer(photo.image, false);
                        if (!imageData) {
                            return null;
                        }
                        return {
                            ...photo,
                            buffer: imageData.buffer,
                            extension: imageData.extension,
                            width: imageData.width,
                            height: imageData.height
                        };
                    })
                );
                
                return processedPhotos.filter(p => p !== null);
            };

            const photoKeys = Object.keys(row).filter(
                key => key.endsWith('Photos') && Array.isArray(row[key])
            );

            const processedPhotoArrays = await Promise.all(
                photoKeys.map(key => processPhotoArray(row[key] || []))
            );

            const processedRow: Record<string, any> = { ...row };
            photoKeys.forEach((key, index) => {
                processedRow[key] = processedPhotoArrays[index];
                const singularKey = key.replace(/Photos$/, 'Photo');
                processedRow[singularKey] = processedPhotoArrays[index].length > 0 
                    ? processedPhotoArrays[index][0] 
                    : null;
            });

            return processedRow;
        })
    );

    return {
        ...data,
        tableRows: processedTableRows
    };
}

function buildImageBufferMap(processedData: { tableRows: Array<Record<string, any>> }) {
    const map = new Map<string, Uint8Array>();
    const sizeMap = new Map<string, [number, number]>();

    const addPhoto = (photo: any) => {
        if (!photo || !photo.image || !(photo.buffer instanceof ArrayBuffer)) return;
        const buffer = new Uint8Array(photo.buffer);
        map.set(photo.image, buffer);
        const fullUrl = photo.image.startsWith('http')
            ? photo.image
            : `${process.env.REACT_APP_S3}${photo.image}`;
        map.set(fullUrl, buffer);
        
        // 如果有尺寸信息，存儲到 sizeMap
        if (photo.width && photo.height) {
            sizeMap.set(photo.image, [photo.width, photo.height]);
            sizeMap.set(fullUrl, [photo.width, photo.height]);
        } else {
            // 嘗試從 buffer 中讀取尺寸
            let extension = 'jpeg';
            const imagePathLower = photo.image.toLowerCase();
            if (imagePathLower.includes('.png')) {
                extension = 'png';
            } else if (imagePathLower.includes('.jpg') || imagePathLower.includes('.jpeg')) {
                extension = 'jpeg';
            }
            const dimensions = getImageDimensionsFromBuffer(photo.buffer, extension);
            if (dimensions) {
                sizeMap.set(photo.image, dimensions);
                sizeMap.set(fullUrl, dimensions);
            }
        }
    };

    processedData.tableRows.forEach((row) => {
        Object.keys(row).forEach((key) => {
            if (key.endsWith('Photos') || key.endsWith('Photo')) {
                const val = (row as any)[key];
                if (Array.isArray(val)) {
                    val.forEach(addPhoto);
                } else {
                    addPhoto(val);
                }
            }
        });
    });

    return { bufferMap: map, sizeMap };
}

export async function generateInspectionDocx(
    templatePath: string,
    data: {
        type: string;
        contractNumber?: string;
        tableRows: Array<Record<string, any>>;
    }
): Promise<Blob> {
    const processedData = await processInspectionData(data);
    const templateResponse = await fetch(templatePath);
    if (!templateResponse.ok) {
        throw new Error(`無法載入模板文件: ${templatePath}`);
    }

    const templateArrayBuffer = await templateResponse.arrayBuffer();
    const zip = new PizZip(templateArrayBuffer);

    zip.file(/\.xml$/).forEach((zipEntry) => {
        let content = zipEntry.asText();
        content = content.replace(/\{([#/^])\s+/g, '{$1');
        content = content.replace(/\s+\}/g, '}');
        zip.file(zipEntry.name, content);
    });

    const { bufferMap: imageBufferMap, sizeMap: imageSizeMap } = buildImageBufferMap(processedData);

    const imageModule = new ImageModule({
        fileType: 'docx',
        centered: false,
        getImage: (tagValue: any): any => {
            let imageObj = tagValue;
            if (Array.isArray(tagValue) && tagValue.length > 0) {
                imageObj = tagValue[0];
            }
            if (!imageObj) {
                return null;
            }
            if (typeof imageObj === 'object' && imageObj.buffer instanceof ArrayBuffer) {
                return new Uint8Array(imageObj.buffer);
            }
            if (typeof imageObj === 'string') {
                const cached = imageBufferMap.get(imageObj);
                if (cached) {
                    return cached;
                }
            }
            if (typeof imageObj === 'object' && imageObj.image) {
                const cached = imageBufferMap.get(imageObj.image);
                if (cached) {
                    return cached;
                }
            }
            return null;
        },
        getSize: (img: any, tagValue: any, tagName?: string) => {
            return [160, 200];
        },
    });
    
    const doc = new Docxtemplater(zip, {
        modules: [imageModule],
        paragraphLoop: true,
        linebreaks: true,
    });

    doc.render(processedData);
    const buffer = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
    });

    return buffer as Blob;
}

async function processCleaningData(data: {
    location: string;
    date: string;
    reportTitle?: string;
    contactPerson: string;
    tel: string;
    email: string;
    contractNumber: string;
    imageTypeInfo?: Array<{ cleaning_image_type_id: number; image_type_name: string; image_type_name_en: string; report_column_name: string }>;
    allImageTypeInfo?: Array<{ cleaning_image_type_id: number; image_type_name: string; image_type_name_en: string; report_column_name: string }>;
    tableRows: Array<Record<string, any>>;
}) {
    const imageTypeInfo = data.imageTypeInfo || [];
    const allImageTypeInfo = data.allImageTypeInfo || [];
    const limitedImageTypeInfo = imageTypeInfo.slice(0, 5);

    // Build a full name lookup map from allImageTypeInfo
    const imageTypeMap = new Map<number, { name: string; nameEn: string; reportColumnName: string }>();
    for (const info of allImageTypeInfo) {
        imageTypeMap.set(info.cleaning_image_type_id, {
            name: info.image_type_name,
            nameEn: info.image_type_name_en,
            reportColumnName: info.report_column_name,
        });
    }
    // Also include configured types (in case they're not in allImageTypeInfo)
    for (const info of limitedImageTypeInfo) {
        if (!imageTypeMap.has(info.cleaning_image_type_id)) {
            imageTypeMap.set(info.cleaning_image_type_id, {
                name: info.image_type_name,
                nameEn: info.image_type_name_en,
                reportColumnName: info.report_column_name,
            });
        }
    }

    const processPhotoArray = async (photos: Array<{ image: string }>) => {
        if (!photos || photos.length === 0) {
            return [];
        }
        
        const processedPhotos = await Promise.all(
            photos.map(async (photo) => {
                if (!photo.image) {
                    return null;
                }
                
                const imageData = await downloadImageToBuffer(photo.image, false);
                if (!imageData) {
                    return null;
                }
                return {
                    ...photo,
                    buffer: imageData.buffer,
                    extension: imageData.extension,
                    width: imageData.width,
                    height: imageData.height
                };
            })
        );
        
        return processedPhotos.filter(p => p !== null);
    };

    const processedTableRows = await Promise.all(
        data.tableRows.map(async (row) => {
            const processedRow: Record<string, any> = { ...row };
            
            if (row.imageGroups && typeof row.imageGroups === 'object') {
                const imageGroups: Record<string, Array<{ image: string }>> = row.imageGroups;
                const processedGroups: Record<string, any> = {};
                
                for (const [typeId, photos] of Object.entries(imageGroups)) {
                    processedGroups[typeId] = await processPhotoArray(photos);
                }
                
                processedRow.imageGroups = processedGroups;

                // ---- MERGE by English name ----
                // Multiple image_type_ids may share the same English name (e.g. afterCleaning for bin_type 1 & 2).
                // Merge their photos into one group per unique English name.
                const mergedByName: Record<string, { photos: any[]; typeName: string; typeNameEn: string }> = {};

                // Merge configured types first (preserve order)
                for (const info of limitedImageTypeInfo) {
                    const nameEn = info.image_type_name_en;
                    const key = String(info.cleaning_image_type_id);
                    const photos = processedGroups[key] || [];
                    if (!mergedByName[nameEn]) {
                        mergedByName[nameEn] = { photos: [], typeName: info.report_column_name, typeNameEn: nameEn };
                    }
                    mergedByName[nameEn].photos = mergedByName[nameEn].photos.concat(photos);
                }

                // Merge extra types found in data
                for (const typeIdStr of Object.keys(processedGroups)) {
                    const typeId = Number(typeIdStr);
                    const typeMeta = imageTypeMap.get(typeId);
                    const nameEn = typeMeta?.nameEn || `type_${typeId}`;
                    const reportColumnName = typeMeta?.reportColumnName || typeMeta?.name || `Type ${typeId}`;
                    if (!mergedByName[nameEn]) {
                        mergedByName[nameEn] = { photos: [], typeName: reportColumnName, typeNameEn: nameEn };
                    }
                    // Only add photos not already included (avoid duplicates if typeId was already in configured)
                    const configuredTypeIds = limitedImageTypeInfo.map(i => i.cleaning_image_type_id);
                    if (configuredTypeIds.indexOf(typeId) === -1) {
                        mergedByName[nameEn].photos = mergedByName[nameEn].photos.concat(processedGroups[typeIdStr] || []);
                    }
                }

                // Build ordered image type list from merged groups
                // Order: configured English names first (in contract order), then extras
                const configuredNameOrder: string[] = [];
                const seenNames = new Set<string>();
                for (const info of limitedImageTypeInfo) {
                    if (!seenNames.has(info.image_type_name_en)) {
                        seenNames.add(info.image_type_name_en);
                        configuredNameOrder.push(info.image_type_name_en);
                    }
                }
                for (const nameEn of Object.keys(mergedByName)) {
                    if (!seenNames.has(nameEn)) {
                        seenNames.add(nameEn);
                        configuredNameOrder.push(nameEn);
                    }
                }

                const allImageTypesOrdered = configuredNameOrder.map(nameEn => {
                    const group = mergedByName[nameEn];
                    return {
                        typeName: group.typeName,
                        typeNameEn: group.typeNameEn,
                        typeId: 0, // merged, no single ID
                        images: group.photos,
                        firstImage: group.photos.length > 0 ? group.photos[0] : null,
                    };
                });
                processedRow.imageTypes = allImageTypesOrdered;

                // Build fixed columns for 1~5 column templates (up to 5, from merged list)
                const colSource = allImageTypesOrdered.slice(0, 5);
                for (let i = 0; i < 5; i++) {
                    const entry = colSource[i];
                    if (entry) {
                        processedRow[`imageCol${i + 1}`] = entry.images;
                        processedRow[`imageCol${i + 1}First`] = entry.firstImage;
                    } else {
                        processedRow[`imageCol${i + 1}`] = [];
                        processedRow[`imageCol${i + 1}First`] = null;
                    }
                }

                // Backward compatibility for existing fixed 4-column templates
                processedRow.beforeImages = processedRow.imageCol1 || [];
                processedRow.afterImages = processedRow.imageCol2 || [];
                processedRow.outsideImages = processedRow.imageCol3 || [];
                processedRow.peopleCounterImage = processedRow.imageCol4 || [];
            }
            
            return processedRow;
        })
    );

    // Collect ALL unique English names across all processed rows (merged by name)
    const allNameEnsInData: string[] = [];
    const seenNameEns = new Set<string>();
    processedTableRows.forEach((row) => {
        if (row.imageTypes && Array.isArray(row.imageTypes)) {
            row.imageTypes.forEach((imgType: any) => {
                const nameEn = imgType.typeNameEn;
                if (nameEn && !seenNameEns.has(nameEn)) {
                    seenNameEns.add(nameEn);
                    allNameEnsInData.push(nameEn);
                }
            });
        }
    });

    // Build the final combined list: configured types first (by English name), then extras found in data
    const configuredNameOrder = new Set<string>();
    for (const info of limitedImageTypeInfo) {
        configuredNameOrder.add(info.image_type_name_en);
    }
    const combinedImageTypes = [
        ...limitedImageTypeInfo.filter((info, idx) => {
            // Deduplicate configured types by English name (keep first occurrence)
            const nameEn = info.image_type_name_en;
            const firstIdx = limitedImageTypeInfo.findIndex(i => i.image_type_name_en === nameEn);
            return idx === firstIdx;
        }),
        ...allNameEnsInData
            .filter(nameEn => !configuredNameOrder.has(nameEn))
            .map(nameEn => {
                const meta = Array.from(imageTypeMap.values()).find(v => v.nameEn === nameEn);
                return {
                    cleaning_image_type_id: 0,
                    image_type_name: meta?.name || nameEn,
                    image_type_name_en: nameEn,
                    report_column_name: meta?.reportColumnName || meta?.name || nameEn,
                };
            }),
    ].slice(0, 5); // Cap at 5 for template columns

    return {
        ...data,
        locationName: data.location,
        imageTypes: combinedImageTypes.map(info => ({
            typeName: info.report_column_name,
            typeNameEn: info.image_type_name_en,
            typeId: info.cleaning_image_type_id,
        })),
        imageTypeCount: combinedImageTypes.length,
        imageTypeName1: combinedImageTypes[0]?.report_column_name || '',
        imageTypeName2: combinedImageTypes[1]?.report_column_name || '',
        imageTypeName3: combinedImageTypes[2]?.report_column_name || '',
        imageTypeName4: combinedImageTypes[3]?.report_column_name || '',
        imageTypeName5: combinedImageTypes[4]?.report_column_name || '',
        imageTypeNameEn1: combinedImageTypes[0]?.image_type_name_en || '',
        imageTypeNameEn2: combinedImageTypes[1]?.image_type_name_en || '',
        imageTypeNameEn3: combinedImageTypes[2]?.image_type_name_en || '',
        imageTypeNameEn4: combinedImageTypes[3]?.image_type_name_en || '',
        imageTypeNameEn5: combinedImageTypes[4]?.image_type_name_en || '',
        tableRows: processedTableRows
    };
}

function buildCleaningImageBufferMap(processedData: { tableRows: Array<Record<string, any>> }) {
    const map = new Map<string, Uint8Array>();
    const sizeMap = new Map<string, [number, number]>();

    const addPhoto = (photo: any) => {
        if (!photo || !photo.image || !(photo.buffer instanceof ArrayBuffer)) return;
        const buffer = new Uint8Array(photo.buffer);
        map.set(photo.image, buffer);
        const fullUrl = photo.image.startsWith('http')
            ? photo.image
            : `${process.env.REACT_APP_S3}${photo.image}`;
        map.set(fullUrl, buffer);
        
        if (photo.width && photo.height) {
            sizeMap.set(photo.image, [photo.width, photo.height]);
            sizeMap.set(fullUrl, [photo.width, photo.height]);
        } else {
            let extension = 'jpeg';
            const imagePathLower = photo.image.toLowerCase();
            if (imagePathLower.includes('.png')) {
                extension = 'png';
            } else if (imagePathLower.includes('.jpg') || imagePathLower.includes('.jpeg')) {
                extension = 'jpeg';
            }
            const dimensions = getImageDimensionsFromBuffer(photo.buffer, extension);
            if (dimensions) {
                sizeMap.set(photo.image, dimensions);
                sizeMap.set(fullUrl, dimensions);
            }
        }
    };

    processedData.tableRows.forEach((row) => {
        if (row.imageGroups && typeof row.imageGroups === 'object') {
            Object.values(row.imageGroups).forEach((photos: any) => {
                if (Array.isArray(photos)) {
                    photos.forEach(addPhoto);
                }
            });
        }
        if (row.imageTypes && Array.isArray(row.imageTypes)) {
            row.imageTypes.forEach((imgType: any) => {
                if (imgType.images && Array.isArray(imgType.images)) {
                    imgType.images.forEach(addPhoto);
                }
                if (imgType.firstImage) {
                    addPhoto(imgType.firstImage);
                }
            });
        }
        Object.keys(row).forEach((key) => {
            if (!/^imageCol\d+(First)?$/.test(key)) return;
            const val = (row as any)[key];
            if (Array.isArray(val)) {
                val.forEach(addPhoto);
                return;
            }
            addPhoto(val);
        });
    });

    return { bufferMap: map, sizeMap };
}

export async function generateDailyCleaningDocx(
    templatePath: string,
    data: {
        location: string;
        date: string;
        reportTitle?: string;
        contactPerson: string;
        tel: string;
        email: string;
        contractNumber: string;
        imageTypeInfo?: Array<{ cleaning_image_type_id: number; image_type_name: string; image_type_name_en: string; report_column_name: string }>;
        allImageTypeInfo?: Array<{ cleaning_image_type_id: number; image_type_name: string; image_type_name_en: string; report_column_name: string }>;
        tableRows: Array<Record<string, any>>;
    }
): Promise<Blob> {
    const processedData = await processCleaningData(data);
    const templateResponse = await fetch(templatePath);
    if (!templateResponse.ok) {
        throw new Error(`無法載入模板文件: ${templatePath}`);
    }

    const templateArrayBuffer = await templateResponse.arrayBuffer();
    const zip = new PizZip(templateArrayBuffer);
    const { bufferMap: imageBufferMap, sizeMap: imageSizeMap } = buildCleaningImageBufferMap(processedData);

    const imageModule = new ImageModule({
        fileType: 'docx',
        centered: false,
        getImage: (tagValue: any): any => {
            let imageObj = tagValue;
            if (Array.isArray(tagValue) && tagValue.length > 0) {
                imageObj = tagValue[0];
            }
            if (!imageObj) {
                return null;
            }
            if (typeof imageObj === 'object' && imageObj.buffer instanceof ArrayBuffer) {
                return new Uint8Array(imageObj.buffer);
            }
            if (typeof imageObj === 'string') {
                const cached = imageBufferMap.get(imageObj);
                if (cached) {
                    return cached;
                }
            }
            if (typeof imageObj === 'object' && imageObj.image) {
                const cached = imageBufferMap.get(imageObj.image);
                if (cached) {
                    return cached;
                }
            }
            return null;
        },
        getSize: (img: any, tagValue: any, tagName?: string) => {
            return [160, 200];
        },
    });
    
    const doc = new Docxtemplater(zip, {
        modules: [imageModule],
        paragraphLoop: true,
        linebreaks: true,
    });

    doc.render(processedData);
    const buffer = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        compression: 'DEFLATE',
    });

    return buffer as Blob;
}

